import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from 'components/AppIcon';

/**
 * RealtimeStatusBadge
 * Shows real-time subscription status for bot_detection_log, simulation_metrics, suspicious_activity
 * Displays latency, retry attempts, and individual channel statuses
 */
const RealtimeStatusBadge = ({
  channelStatuses = {},
  connectionStatus = 'idle',
  allConnected = false,
  retryCounts = {},
  latencyMs = null,
  onReconnectNow,
}) => {
  const [expanded, setExpanded] = useState(false);

  const MAX_RETRIES = 8;

  const channels = [
    { key: 'botDetection', label: 'bot_detection_log' },
    { key: 'simMetrics', label: 'simulation_metrics' },
    { key: 'cheatDetection', label: 'suspicious_activity' },
  ];

  const getStatusConfig = () => {
    if (allConnected) {
      return {
        bg: 'bg-green-500/10',
        border: 'border-green-500/25',
        text: 'text-green-400',
        dot: 'bg-green-400',
        pulse: true,
        label: 'Connected',
        icon: 'Wifi',
      };
    }
    if (connectionStatus === 'connecting') {
      return {
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/25',
        text: 'text-yellow-400',
        dot: 'bg-yellow-400',
        pulse: true,
        label: 'Reconnecting...',
        icon: 'RefreshCw',
        spinning: true,
      };
    }
    return {
      bg: 'bg-red-500/10',
      border: 'border-red-500/25',
      text: 'text-red-400',
      dot: 'bg-red-400',
      pulse: false,
      label: 'Offline',
      icon: 'WifiOff',
    };
  };

  const getChannelStatusConfig = (status) => {
    if (status === 'connected') return { color: 'text-green-400', dot: 'bg-green-400', label: 'Connected' };
    if (status === 'connecting') return { color: 'text-yellow-400', dot: 'bg-yellow-400', label: 'Reconnecting' };
    if (status === 'error' || status === 'closed') return { color: 'text-red-400', dot: 'bg-red-400', label: 'Offline' };
    return { color: 'text-muted-foreground', dot: 'bg-gray-500', label: 'Idle' };
  };

  const cfg = getStatusConfig();

  // Get the max retry count across all channels
  const maxRetry = Math.max(...Object.values(retryCounts || {}), 0);
  const hasRetries = maxRetry > 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Main Badge Row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status Badge */}
        <div
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold ${
            cfg?.bg
          } ${cfg?.border} ${cfg?.text}`}
        >
          {/* Dot */}
          <span className="relative flex h-2 w-2">
            {cfg?.pulse && (
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${cfg?.dot}`}
              />
            )}
            <span className={`relative inline-flex rounded-full h-2 w-2 ${cfg?.dot}`} />
          </span>

          {/* Icon + Label */}
          <Icon
            name={cfg?.icon}
            size={14}
            className={`${cfg?.spinning ? 'animate-spin' : ''} ${cfg?.text}`}
          />
          <span>{cfg?.label}</span>
        </div>

        {/* Latency Badge */}
        {latencyMs !== null && allConnected && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-surface border-border">
            <Icon name="Clock" size={12} className="text-blue-400" />
            <span className="text-xs text-muted-foreground">Latency:</span>
            <span
              className={`text-xs font-mono font-bold ${
                latencyMs < 100 ? 'text-green-400' : latencyMs < 300 ? 'text-yellow-400' : 'text-red-400'
              }`}
            >
              {latencyMs}ms avg
            </span>
          </div>
        )}

        {/* Retry Badge */}
        {hasRetries && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-yellow-500/5 border-yellow-500/20">
            <Icon name="RefreshCw" size={12} className="text-yellow-400" />
            <span className="text-xs text-yellow-400 font-medium">
              Attempt {maxRetry}/{MAX_RETRIES}
            </span>
            {/* Backoff progress */}
            <div className="flex gap-0.5">
              {Array.from({ length: MAX_RETRIES })?.map((_, i) => (
                <div
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${
                    i < maxRetry ? 'bg-yellow-400' : 'bg-border'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Reconnect Now Button */}
        {(!allConnected || hasRetries) && onReconnectNow && (
          <button
            onClick={onReconnectNow}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:bg-primary/20 bg-primary/10 border-primary/25 text-primary"
          >
            <Icon name="Zap" size={12} />
            Reconnect Now
          </button>
        )}

        {/* Expand Toggle */}
        <button
          onClick={() => setExpanded((p) => !p)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
        >
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
          {expanded ? 'Hide' : 'Details'}
        </button>
      </div>
      {/* Expanded Panel */}
      {expanded && (
        <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Subscription Channels</p>
          {channels?.map(({ key, label }) => {
            const status = channelStatuses?.[key] || 'idle';
            const retryCount = retryCounts?.[key] || 0;
            const chCfg = getChannelStatusConfig(status);
            return (
              <div
                key={key}
                className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <span className={`relative flex h-1.5 w-1.5`}>
                    {(status === 'connected' || status === 'connecting') && (
                      <span
                        className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${chCfg?.dot}`}
                      />
                    )}
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${chCfg?.dot}`} />
                  </span>
                  <span className="text-xs font-mono text-foreground">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {retryCount > 0 && (
                    <span className="text-xs text-yellow-400 font-mono">retry {retryCount}/{MAX_RETRIES}</span>
                  )}
                  <span className={`text-xs font-semibold ${chCfg?.color}`}>{chCfg?.label}</span>
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground pt-1">
            Backoff: 1.5s → 2.25s → 3.4s → 5.1s → 7.6s → 11.4s → 17.1s → 25.6s
          </p>
        </div>
      )}
    </div>
  );
};

export default RealtimeStatusBadge;
